import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

const projects = [
  {
    id: 'nochotspot',
    title: 'NOC System — Hotspot Billing Platform',
    desc: 'Enterprise-grade NAT operation system with automated hotspot billing, integrated Mpesa STK push, network troubleshooting support, and secure real-time network monitoring. Built with Java Swing, React.js and RadiusNetty.',
    tags: ['React', 'Java Swing', 'PostgreSQL', 'Radius Netty'],
    url: 'https://atomichotspotapplication.onrender.com/',
    hasLive: true,
    icon: '🛰️',
    accent: '#00ffff',
    bgDark: '#0a1a1a',
    bgLight: '#e0f7fa',
  },
  {
    id: 'furnishers',
    title: 'Atomic Furnishers E-commerce System',
    desc: 'Full-stack Next.js e-commerce platform with 20+ admin users, multi-client support, Google Maps integration, and an online marketplace for furniture and furnishing services.',
    tags: ['Next.js', 'TypeScript', 'Tailwind CSS', 'MongoDB'],
    url: 'https://atomicfurnisherske.vercel.app',
    hasLive: true,
    icon: '🪑',
    accent: '#ff6b6b',
    bgDark: '#1a0a0a',
    bgLight: '#fce4ec',
  },
  {
    id: 'ddd',
    title: 'DDD Taskforce',
    desc: 'Centralized task coordination system with auto user management, performance scaling, time tracking, and tools designed to eliminate work latency across distributed teams.',
    tags: ['React.js', 'Python', 'FastAPI', 'PostgreSQL', 'Docker'],
    url: null,
    hasLive: false,
    icon: '⚡',
    accent: '#69f0ae',
    bgDark: '#0a1a0d',
    bgLight: '#e8f5e9',
  },
  {
    id: 'datastream',
    title: 'DataStream Analytics',
    desc: 'Real-time data processing platform with ML-powered insights, custom dashboards, and API integration built for enterprise clients at scale.',
    tags: ['React', 'Python', 'Kafka', 'TensorFlow'],
    url: null,
    hasLive: false,
    icon: '📊',
    accent: '#b388ff',
    bgDark: '#0a0d1a',
    bgLight: '#ede7f6',
  },
  {
    id: 'cloudops',
    title: 'CloudOps Orchestrator',
    desc: 'Multi-cloud orchestration platform featuring automated scaling, cost optimization, and infrastructure-as-code deployment pipelines across AWS and more.',
    tags: ['Go', 'Kubernetes', 'Terraform', 'AWS'],
    url: null,
    hasLive: false,
    icon: '☁️',
    accent: '#40c4ff',
    bgDark: '#060d1a',
    bgLight: '#e3f2fd',
  },
  {
    id: 'pentest',
    title: 'PenTest Framework',
    desc: 'Comprehensive penetration testing framework with automated exploit chains, payload generation, and detailed security reporting for ethical hacking engagements.',
    tags: ['Python', 'Bash', 'Metasploit', 'Burp Suite'],
    url: null,
    hasLive: false,
    icon: '🔐',
    accent: '#ff5252',
    bgDark: '#1a0505',
    bgLight: '#fbe9e7',
  },
];

const THUM_BASE = 'https://image.thum.io/get/width/600/crop/400/noanimate/';

const PinCard = ({ project, isDark }) => {
  const [imgState, setImgState] = useState('loading'); // 'loading' | 'loaded' | 'error'
  const cardBg = isDark ? '#161b22' : '#ffffff';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const titleColor = isDark ? '#e6edf3' : '#111';
  const descColor = isDark ? '#8b949e' : '#555';
  const tagBg = isDark ? '#21262d' : '#f0f0f0';
  const tagColor = isDark ? '#8b949e' : '#444';

  useEffect(() => {
    if (!project.url) return;
    const img = new window.Image();
    img.onload = () => setImgState('loaded');
    img.onerror = () => setImgState('error');
    img.src = THUM_BASE + project.url;
  }, [project.url]);

  const placeholderStyle = {
    height: 160,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: isDark ? project.bgDark : project.bgLight,
    fontSize: 48,
    borderRadius: '12px 12px 0 0',
  };

  return (
    <Box
      sx={{
        background: cardBg,
        border: `1px solid ${border}`,
        borderRadius: '16px',
        overflow: 'hidden',
        breakInside: 'avoid',
        mb: '14px',
        transition: 'transform 0.22s ease, box-shadow 0.22s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: isDark
            ? `0 12px 32px rgba(0,0,0,0.5), 0 0 0 1px ${project.accent}55`
            : `0 12px 32px rgba(0,0,0,0.12), 0 0 0 1px ${project.accent}66`,
        },
      }}
    >
      {/* Image area */}
      {project.url && imgState === 'loaded' ? (
        <img
          src={THUM_BASE + project.url}
          alt={project.title}
          style={{ width: '100%', display: 'block', borderRadius: '16px 16px 0 0' }}
        />
      ) : (
        <Box style={placeholderStyle}>
          <span>{project.icon}</span>
        </Box>
      )}

      {/* Body */}
      <Box sx={{ p: '14px 16px 16px' }}>
        {/* Live badge */}
        {project.hasLive && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: '10px',
              fontFamily: 'JetBrains Mono, monospace',
              color: '#00ffff',
              background: 'rgba(0,255,255,0.08)',
              border: '1px solid rgba(0,255,255,0.25)',
              borderRadius: '100px',
              px: '8px',
              py: '2px',
              mb: '8px',
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#00ffff',
                boxShadow: '0 0 6px #00ffff',
              }}
            />
            live
          </Box>
        )}

        <Typography
          sx={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '13px',
            fontWeight: 700,
            color: titleColor,
            mb: '6px',
            lineHeight: 1.4,
          }}
        >
          {project.title}
        </Typography>

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '12px',
            color: descColor,
            lineHeight: 1.6,
            mb: '10px',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {project.desc}
        </Typography>

        {/* Tags */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', mb: '12px' }}>
          {project.tags.map((tag) => (
            <Box
              key={tag}
              component="span"
              sx={{
                fontSize: '10px',
                fontFamily: 'JetBrains Mono, monospace',
                px: '7px',
                py: '2px',
                borderRadius: '100px',
                background: tagBg,
                color: tagColor,
                border: `1px solid ${border}`,
              }}
            >
              {tag}
            </Box>
          ))}
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {project.url && (
            <Box
              component="a"
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontSize: '11px',
                fontFamily: 'JetBrains Mono, monospace',
                px: '10px',
                py: '5px',
                borderRadius: '8px',
                border: `1px solid ${project.accent}55`,
                color: project.accent,
                background: `${project.accent}10`,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'background 0.15s',
                '&:hover': { background: `${project.accent}20` },
              }}
            >
              ↗ launch
            </Box>
          )}
          <Box
            component="button"
            sx={{
              fontSize: '11px',
              fontFamily: 'JetBrains Mono, monospace',
              px: '10px',
              py: '5px',
              borderRadius: '8px',
              border: `1px solid rgba(255,0,51,0.35)`,
              color: '#ff0033',
              background: 'rgba(255,0,51,0.06)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              transition: 'background 0.15s',
              '&:hover': { background: 'rgba(255,0,51,0.14)' },
            }}
          >
            {'</>'}  code
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const Projects = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sectionBg = isDark ? '#0d1117' : '#f4f5f7';
  const accentCyan = '#00ffff';
  const accentRed = '#ff0033';

  // Split into 3 columns for masonry
  const columns = [[], [], []];
  projects.forEach((p, i) => columns[i % 3].push(p));

  return (
    <Box component="section" id="projects" sx={{ bgcolor: sectionBg, py: 10 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Typography
          variant="h2"
          sx={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: { xs: '28px', md: '44px' },
            fontWeight: 700,
            textAlign: 'center',
            mb: 1,
          }}
        >
          <span style={{ color: accentCyan }}>$</span>
          <span style={{ color: accentRed }}> ls</span>
          <span style={{ color: isDark ? '#e6edf3' : '#111' }}> projects/</span>
        </Typography>
        <Typography
          sx={{
            textAlign: 'center',
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            color: isDark ? '#8b949e' : '#777',
            mb: 6,
          }}
        >
          A selection of live systems and shipped products
        </Typography>

        {/* Pinterest masonry grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: '14px',
            alignItems: 'start',
          }}
        >
          {columns.map((col, ci) => (
            <Box key={ci} sx={{ display: 'flex', flexDirection: 'column' }}>
              {col.map((project) => (
                <PinCard key={project.id} project={project} isDark={isDark} />
              ))}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Projects;
